// components/Achievements.js
import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faMicrochip,
  faMobileAlt,
  faDatabase,
  faPlug,
  faBrain,
  faClock,
  faChartBar,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import project1 from '../images/electricite_1000x1000.webp';
import project2 from '../images/domotique_projet.jpeg_1000x1000.webp';
import project3 from '../images/Chiefdom_projet_1000x1000.webp';
import project4 from '../images/gilresBTP_compress_1000x1000.webp';
import './Achievements.css';

const Achievements = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const projects = [
    {
      image: project1,
      title: t('project_electrical_title'),
      description: t('project_electrical_description'),
      category: t('project_electrical_category'),
      tech: t('project_electrical_tech').split(',').map(s => s.trim()),
      time: t('project_electrical_time'),
      impact: t('project_electrical_impact'),
      icon: faPlug
    },
    {
      image: project2,
      title: t('project_domotique_title'),
      description: t('project_domotique_description'),
      category: t('project_domotique_category'),
      tech: t('project_domotique_tech').split(',').map(s => s.trim()),
      time: t('project_domotique_time'),
      impact: t('project_domotique_impact'),
      icon: faMicrochip
    },
    {
      image: project3,
      title: t('project_chiefdom_title'),
      description: t('project_chiefdom_description'),
      category: t('project_chiefdom_category'),
      tech: t('project_chiefdom_tech').split(',').map(s => s.trim()),
      time: t('project_chiefdom_time'),
      impact: t('project_chiefdom_impact'),
      icon: faMobileAlt
    },
    {
      image: project4,
      title: t('project_batiment_title'),
      description: t('project_batiment_description'),
      category: t('project_batiment_category'),
      tech: t('project_batiment_tech').split(',').map(s => s.trim()),
      time: t('project_batiment_time'),
      impact: t('project_batiment_impact'),
      icon: faDatabase
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40,
      scale: 0.96
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <section className="achievements-section" ref={ref}>
      <div className="container-fluid">
        <div className="section-header">
          <motion.span
            className="section-tag"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <FontAwesomeIcon icon={faBrain} /> {t('titre_Nos_realisation')}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('msg_Nos_realisation')}
          </motion.h2>
        </div>

        <motion.div
          className="achievements-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="achievement-card"
              variants={cardVariants}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3 }}
            >
              <div className="card-image">
                <img src={project.image} alt={project.title} />
                <div className="card-tag">
                  <FontAwesomeIcon icon={faTag} />
                  <span>{project.category}</span>
                </div>
                <div className="card-stats">
                  <div className="stat-item">
                    <FontAwesomeIcon icon={faClock} />
                    <span>{project.time}</span>
                  </div>
                  <div className="stat-item">
                    <FontAwesomeIcon icon={faChartBar} />
                    <span>{project.impact}</span>
                  </div>
                </div>
              </div>

              <div className="card-content">
                <div className="card-header">
                  <FontAwesomeIcon icon={project.icon} className="card-icon" />
                  <h3>{project.title}</h3>
                </div>
                <p>{project.description}</p>
                <div className="card-footer">
                  <div className="tech-stack">
                    {project.tech.map((tech, i) => (
                      <span key={i} className="tech-pill">{tech}</span>
                    ))}
                  </div>
                  <motion.a
                    href="#"
                    className="btn-achievement"
                    whileHover={{ x: 6 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span>Voir</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Achievements;